import styled from 'styled-components'
import { chartFont, widgetFont } from '../../widgetTypography'

/*
 * The pieces the recharts widgets share: a themed tooltip, a legend that can
 * hide a series, and the axis styling.
 */

const TooltipBox = styled.div`
    background: ${p => p.theme.card.background};
    color: ${p => p.theme.font.color};
    border: 1px solid ${p => p.theme.table.border};
    border-radius: 0.5rem;
    box-shadow: ${p => p.theme.card.boxShadow};
    padding: 0.45rem 0.6rem;
    ${widgetFont.label}
    min-width: 8rem;
    max-width: 16rem;
`

const TooltipTitle = styled.div`
    font-weight: 600;
    margin-bottom: 0.25rem;
`

const TooltipRow = styled.div`
    display: flex;
    align-items: center;
    gap: 0.4rem;
    line-height: 1.5;

    & > span:nth-child(2) {
        flex: 1;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        color: ${p => p.theme.font.label};
    }
    & > b { font-variant-numeric: tabular-nums; }
`

export const Swatch = styled.span`
    flex-shrink: 0;
    width: 0.6rem;
    height: 0.6rem;
    border-radius: ${p => p.$shape === 'line' ? '1px' : '50%'};
    ${p => p.$shape === 'line' ? 'height: 2px; width: 0.8rem;' : ''}
    background: ${p => p.$color};
    ${p => p.$dashed ? `background: repeating-linear-gradient(90deg, ${p.$color} 0 3px, transparent 3px 5px);` : ''}
`

/*
 * `rows` is [{ color, name, value, dashed? }] already formatted; `title` the
 * bucket or category. Recharts hands its own payload to `content`; each widget
 * turns it into rows, since only it knows what its numbers mean.
 */
export const TooltipCard = ({ title, rows = [], footer }) => (
    <TooltipBox role="status">
        {title && <TooltipTitle>{title}</TooltipTitle>}
        {rows.map((r, i) => (
            <TooltipRow key={i}>
                {r.color ? <Swatch $color={r.color} $dashed={r.dashed} /> : <span />}
                <span>{r.name}</span>
                <b>{r.value}</b>
            </TooltipRow>
        ))}
        {footer}
    </TooltipBox>
)

const LegendList = styled.ul`
    display: flex;
    flex-wrap: wrap;
    gap: 0.2rem 0.9rem;
    margin: 0;
    padding: 0;
    list-style: none;
    flex-shrink: 0;
    ${widgetFont.label}
`

const LegendButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    font: inherit;
    color: ${p => p.theme.font.color};
    background: none;
    border: none;
    padding: 0.1rem 0.15rem;
    border-radius: 0.25rem;
    cursor: ${p => p.disabled ? 'default' : 'pointer'};
    opacity: ${p => p.$off ? 0.45 : 1};
    text-decoration: ${p => p.$off ? 'line-through' : 'none'};

    &:focus-visible { outline: 2px solid ${p => p.theme.font.highlight}; outline-offset: 1px; }
`

/*
 * Each item toggles its series when `onToggle` is given — a pressed button
 * rather than a coloured dot, so it can be reached and read without a mouse.
 * The last visible series cannot be hidden: an empty chart answers nothing.
 */
export const Legend = ({ items, hidden = [], onToggle }) => {
    const visible = items.filter(i => !hidden.includes(i.id)).length
    return (
        <LegendList>
            {items.map(item => {
                const off = hidden.includes(item.id)
                return (
                    <li key={item.id}>
                        <LegendButton type="button" $off={off} aria-pressed={!off}
                            disabled={!onToggle || (!off && visible === 1)}
                            onClick={() => onToggle?.(item.id)}>
                            <Swatch $color={item.color} $shape={item.shape} $dashed={item.dashed} />
                            {item.name}
                        </LegendButton>
                    </li>
                )
            })}
        </LegendList>
    )
}

// Axis styling every chart shares: no axis line, muted small ticks
export const axisProps = (palette) => ({
    axisLine: false,
    tickLine: false,
    tick: { fontSize: chartFont.tick, fill: palette.axis },
})

// The chart frame inside the card body — recharts needs a box with a size
export const ChartBox = styled.div`
    position: relative;
    flex: 1 1 auto;
    min-height: 0;
    width: 100%;

    & > div { position: absolute; inset: 0; }

    .recharts-surface:focus { outline: none; }
    .recharts-wrapper:focus-visible { outline: 2px solid ${p => p.theme.font.highlight}; outline-offset: 2px; border-radius: 0.25rem; }
`
